import { href, Link } from 'react-router'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/mcp-server.ts'

// The page lives at /mcp-server rather than /mcp because /mcp is the protocol
// endpoint itself (app/routes/mcp.ts). The longer path is also the better
// search target: "mcp server" is what someone types, "mcp" alone is not.
export const meta: Route.MetaFunction = ({ matches }) =>
	buildMeta({
		title: 'MCP Server for GDPR, CCPA and WCAG Compliance - Syrcha',
		description:
			'Connect your coding agent to Syrcha over MCP. List your sites, read failing compliance rules with evidence and remediation, and trigger a rescan when the fix ships. Free on every tier.',
		matches,
	})

export default function McpServerRoute() {
	// No `<main>`: the marketing layout owns the single main landmark.
	return (
		<>
			<Intro />
			<Loop />
			<Tools />
			<Fence />
			<Connect />
			<EndCTA />
		</>
	)
}

function Intro() {
	return (
		<section className="relative isolate overflow-hidden bg-[var(--marketing-base)] pt-16 pb-24 sm:pt-24 sm:pb-28">
			<div
				aria-hidden
				className="pointer-events-none absolute -top-40 right-[-10%] h-[420px] w-[520px] rounded-full opacity-30"
				style={{
					background:
						'radial-gradient(closest-side, rgba(103,232,249,0.22), rgba(103,232,249,0) 70%)',
				}}
			/>
			<div aria-hidden className="grain" />

			<div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ mcp</p>

				<div className="mt-4 grid items-start gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
					<div className="max-w-2xl">
						<h1
							className="font-[family-name:var(--font-display)] text-[44px] leading-[1.02] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[56px] lg:text-[64px]"
							style={{ textWrap: 'balance' }}
						>
							Hand the compliance failures to the{' '}
							<span className="accent-underline">agent</span> that wrote them.
						</h1>

						<p
							className="mt-7 max-w-xl text-[17px] leading-relaxed text-[var(--marketing-gray)]"
							style={{ textWrap: 'pretty' }}
						>
							Syrcha runs a hosted MCP server. Your coding agent can list your
							sites, read exactly which rules are failing and why, pull the
							evidence, and trigger a rescan once the fix ships.
						</p>

						<div className="mt-10 flex flex-wrap items-center gap-3">
							<Link to={href('/signup')} className="btn-marketing">
								<span className="flex items-center gap-2">
									Connect your agent
									<span aria-hidden>→</span>
								</span>
							</Link>
							<Link to="#tools" className="btn-marketing-outline">
								See the tools
							</Link>
						</div>

						{/* The generous fact is the one most likely to be disbelieved, so
						    it sits under the button rather than in a pricing table three
						    scrolls down. */}
						<p className="mt-4 text-[13px] text-[var(--marketing-faint)]">
							Free on every tier - including the tiers where our REST API is
							not.
						</p>

						<dl className="mt-12 grid grid-cols-2 gap-x-8 gap-y-5 border-t border-[var(--marketing-border)] pt-6 font-[family-name:var(--font-mono)] text-[12px] sm:grid-cols-3 sm:gap-y-1">
							{[
								{ k: 'Transport', v: 'Streamable HTTP' },
								{ k: 'Auth', v: 'OAuth 2.1' },
								{ k: 'Tools', v: '5' },
								{ k: 'Destructive verbs', v: 'None' },
								{ k: 'Install', v: 'Nothing to install' },
								{ k: 'Price', v: 'Free, all tiers' },
							].map((r) => (
								<div key={r.k}>
									<dt className="text-[var(--marketing-faint)]">{r.k}</dt>
									<dd className="mt-1 text-[14px] text-[var(--marketing-black)]">
										{r.v}
									</dd>
								</div>
							))}
						</dl>
					</div>

					<AgentCard />
				</div>
			</div>
		</section>
	)
}

/**
 * The product, not a vector blob - same principle as the home page's ScanCard.
 * This is a transcript shape rather than a scan result, because what is being
 * sold here is the loop, and the loop is a conversation.
 */
function AgentCard() {
	return (
		<div className="relative">
			<div className="marketing-card overflow-hidden">
				<div className="flex items-center justify-between border-b border-[var(--marketing-border)] px-4 py-3">
					<div className="flex items-center gap-2 font-[family-name:var(--font-mono)] text-[12px] text-[var(--marketing-gray)]">
						<span className="text-[var(--marketing-accent)]">syrcha</span>
						<span className="text-[var(--marketing-faint)]">mcp</span>
						<span className="rounded bg-[var(--marketing-surface)] px-2 py-0.5 text-[11px] text-[var(--marketing-black)]">
							acme.example.com
						</span>
					</div>
					<span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--marketing-faint)]">
						connected
					</span>
				</div>

				<div className="space-y-3 px-4 py-4 font-[family-name:var(--font-mono)] text-[12px]">
					<ToolCall
						name="get_failing_rules"
						result="3 rules failing · ordered by weight"
					/>

					<div className="rounded border border-[var(--marketing-border)] bg-[var(--marketing-surface)] px-3 py-2.5">
						<p className="text-[var(--marketing-danger)]">
							ccpa-012 · weight 9
						</p>
						<p className="mt-1 leading-relaxed text-[var(--marketing-black)]">
							No "Do Not Sell or Share My Personal Information" link on 4 pages
						</p>
						<p className="mt-1.5 leading-relaxed text-[var(--marketing-gray)]">
							fix: add the link to the global footer, label matched verbatim
						</p>
					</div>

					<div className="rounded border border-[var(--marketing-border)] bg-[var(--marketing-surface)] px-3 py-2.5">
						<p className="text-[var(--marketing-warn)]">
							gdpr-004 · declaration-blocked
						</p>
						<p className="mt-1 leading-relaxed text-[var(--marketing-black)]">
							Jurisdiction not declared - cannot be fixed in code
						</p>
					</div>

					<ToolCall name="get_rule_evidence" result="selector + snippet" />
					<ToolCall name="trigger_scan" result="queued · scan_8f21" />
				</div>

				<div className="flex items-center justify-between border-t border-[var(--marketing-border)] px-4 py-3 font-[family-name:var(--font-mono)] text-[11px] text-[var(--marketing-faint)]">
					<span>rule set v11</span>
					<span>last complete · 6m ago</span>
				</div>
			</div>
		</div>
	)
}

function ToolCall({ name, result }: { name: string; result: string }) {
	return (
		<div className="flex items-baseline gap-2">
			<span aria-hidden className="text-[var(--marketing-accent)]">
				→
			</span>
			<span className="text-[var(--marketing-black)]">{name}</span>
			<span className="text-[var(--marketing-faint)]">{result}</span>
		</div>
	)
}

function Loop() {
	const steps = [
		{
			n: '01',
			t: 'Ask what is failing',
			d: 'One entry per failing rule, ordered by weight so the agent starts where the score actually moves. Not one row per rule per page - a missing consent banner on twenty pages is one fix, not twenty.',
		},
		{
			n: '02',
			t: 'Read the evidence',
			d: 'The selector, the snippet and the reason behind a finding, so the agent edits the right element instead of guessing from a rule name.',
		},
		{
			n: '03',
			t: 'Ship the fix',
			d: 'The part we stay out of. Your agent works in your codebase, with your review process.',
		},
		{
			n: '04',
			t: 'Trigger the rescan',
			d: 'Returns immediately with a scan id rather than blocking for minutes. Every later read states its own freshness, so the agent knows whether it is looking at the fix or at the run before it.',
		},
	]

	return (
		<section
			id="loop"
			className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-20 sm:py-24"
		>
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ the loop</p>
				<h2
					className="mt-4 max-w-2xl font-[family-name:var(--font-display)] text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
					style={{ textWrap: 'balance' }}
				>
					Find it, fix it, prove it - without leaving the editor.
				</h2>

				<div className="mt-14 grid gap-px border border-[var(--marketing-border)] bg-[var(--marketing-border)] sm:grid-cols-2 lg:grid-cols-4">
					{steps.map((s) => (
						<div key={s.n} className="bg-[var(--marketing-base)] p-6">
							<p className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.16em] text-[var(--marketing-accent)]">
								{s.n}
							</p>
							<h3 className="mt-3 text-[17px] font-semibold text-[var(--marketing-black)]">
								{s.t}
							</h3>
							<p className="mt-2 text-[14px] leading-relaxed text-[var(--marketing-gray)]">
								{s.d}
							</p>
						</div>
					))}
				</div>

				{/* Undetermined rows are the non-obvious half of this and the part an
				    agent gets catastrophically wrong if we do not say it out loud. */}
				<div className="mt-10 max-w-3xl border-l-2 border-[var(--marketing-accent)] pl-5">
					<p className="text-[15px] leading-relaxed text-[var(--marketing-gray)]">
						Findings that <em>cannot</em> be fixed in code come back tagged as
						such. GDPR applicability is something you declare, never something
						we infer from your pages - so an agent handed that row untagged
						would try to fix it by editing files, forever. It is marked
						declaration-blocked instead, and pointed at the setting that
						resolves it.
					</p>
				</div>
			</div>
		</section>
	)
}

/**
 * The tools this page advertises. Exported so `mcp-server.test.ts` can hold it
 * against what the MCP server actually registers - a marketing page that names
 * a tool the server does not expose is the failure this product exists to not
 * commit.
 */
export const MCP_TOOLS: Array<{
	name: string
	scope: 'read' | 'write'
	d: string
}> = [
	{
		name: 'list_websites',
		scope: 'read',
		d: 'The sites on your account, with their verification state.',
	},
	{
		name: 'get_failing_rules',
		scope: 'read',
		d: 'Failing and undetermined rules for the latest completed scan, weight-ordered, each with remediation guidance and an affected-page count.',
	},
	{
		name: 'get_rule_evidence',
		scope: 'read',
		d: 'The captured evidence behind one finding - selector, snippet, context.',
	},
	{
		name: 'trigger_scan',
		scope: 'write',
		d: 'Queue a rescan. Returns a scan id immediately; never blocks.',
	},
	{
		name: 'create_website',
		scope: 'write',
		d: 'Add a site and return the verification TXT record to publish.',
	},
]

function Tools() {
	const tools = MCP_TOOLS

	return (
		<section
			id="tools"
			className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-20 sm:py-24"
		>
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ tools</p>
				<h2
					className="mt-4 max-w-2xl font-[family-name:var(--font-display)] text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
					style={{ textWrap: 'balance' }}
				>
					Five tools. No resources, no prompts.
				</h2>
				<p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[var(--marketing-gray)]">
					The tool list is a deliberate subset of our REST API rather than a
					mirror of it. Everything an agent needs to close the loop, and nothing
					it needs to be trusted with.
				</p>

				<ul className="mt-12 divide-y divide-[var(--marketing-border)] border-y border-[var(--marketing-border)]">
					{tools.map((t) => (
						<li
							key={t.name}
							className="grid gap-2 py-5 sm:grid-cols-[240px_1fr] sm:gap-8"
						>
							<div className="flex items-baseline gap-3">
								<code className="font-[family-name:var(--font-mono)] text-[13px] text-[var(--marketing-black)]">
									{t.name}
								</code>
								<span
									className={`font-[family-name:var(--font-mono)] text-[10.5px] tracking-[0.12em] uppercase ${
										t.scope === 'write'
											? 'text-[var(--marketing-warn)]'
											: 'text-[var(--marketing-faint)]'
									}`}
								>
									{t.scope}
								</span>
							</div>
							<p className="text-[15px] leading-relaxed text-[var(--marketing-gray)]">
								{t.d}
							</p>
						</li>
					))}
				</ul>
			</div>
		</section>
	)
}

function Fence() {
	return (
		<section className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-20 sm:py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
					<div>
						<p className="eyebrow text-[var(--marketing-faint)]">/ the fence</p>
						<h2
							className="mt-4 font-[family-name:var(--font-display)] text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[40px]"
							style={{ textWrap: 'balance' }}
						>
							What it deliberately cannot do.
						</h2>
						<div className="mt-6 space-y-5 text-[16px] leading-relaxed text-[var(--marketing-gray)]">
							<p>
								Pointing an autonomous agent at a service that holds your
								account is a real decision, and the honest way to make it easier
								is to narrow what the credential can reach rather than to
								reassure you about it.
							</p>
							<p>
								There is no delete verb, no billing surface, no user
								administration and no way to reach the rest of the API through
								this connection. A rescan and a new site are the only writes
								that exist, because those are the two the loop genuinely needs.
							</p>
							<p>
								Every tool call is one audited request with its own request id,
								exactly like an API call - so if you ever want to know what your
								agent did, the log is the same log.
							</p>
						</div>
					</div>

					<div className="marketing-card p-6">
						<p className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.14em] text-[var(--marketing-faint)] uppercase">
							Out of scope
						</p>
						<ul className="mt-4 space-y-3 font-[family-name:var(--font-mono)] text-[13px]">
							{[
								'Deleting anything',
								'Reading or changing billing',
								'Managing users or tokens',
								'MCP resources and prompts',
								'The wider REST API',
							].map((item) => (
								<li key={item} className="flex items-center gap-3">
									<span
										aria-hidden
										className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-sm border"
										style={{ borderColor: 'var(--marketing-faint)' }}
									>
										<svg
											viewBox="0 0 10 10"
											className="h-2.5 w-2.5"
											fill="none"
										>
											<path
												d="M2 5H8"
												stroke="var(--marketing-faint)"
												strokeWidth="1.6"
												strokeLinecap="round"
											/>
										</svg>
									</span>
									<span className="text-[var(--marketing-gray)]">{item}</span>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	)
}

function Connect() {
	return (
		<section className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-20 sm:py-24">
			<div className="mx-auto max-w-6xl px-6 lg:px-8">
				<p className="eyebrow text-[var(--marketing-faint)]">/ connect</p>
				<h2
					className="mt-4 max-w-2xl font-[family-name:var(--font-display)] text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
					style={{ textWrap: 'balance' }}
				>
					A browser click, not a pasted secret.
				</h2>
				<p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[var(--marketing-gray)]">
					The server is hosted, so there is nothing to install and nothing to
					keep up to date. Your client registers itself and you approve the
					connection in the browser - no token ever passes through your
					clipboard.
				</p>

				<ol className="mt-12 grid gap-px border border-[var(--marketing-border)] bg-[var(--marketing-border)] sm:grid-cols-3">
					{[
						{
							n: '01',
							t: 'Open Agent (MCP)',
							d: 'In your Syrcha dashboard. It shows the endpoint and the clients that are connected.',
						},
						{
							n: '02',
							t: 'Add it to your client',
							d: 'Claude, Cursor, or anything else speaking MCP over streamable HTTP.',
						},
						{
							n: '03',
							t: 'Approve in the browser',
							d: 'The grant becomes a scoped connection you can revoke from the same panel.',
						},
					].map((s) => (
						<li key={s.n} className="bg-[var(--marketing-base)] p-6">
							<p className="font-[family-name:var(--font-mono)] text-[11px] tracking-[0.16em] text-[var(--marketing-accent)]">
								{s.n}
							</p>
							<h3 className="mt-3 text-[17px] font-semibold text-[var(--marketing-black)]">
								{s.t}
							</h3>
							<p className="mt-2 text-[14px] leading-relaxed text-[var(--marketing-gray)]">
								{s.d}
							</p>
						</li>
					))}
				</ol>
			</div>
		</section>
	)
}

function EndCTA() {
	return (
		<section className="border-t border-[var(--marketing-border)] bg-[var(--marketing-base)] py-20 sm:py-28">
			<div className="mx-auto max-w-6xl px-6 text-center lg:px-8">
				<h2
					className="mx-auto max-w-2xl font-[family-name:var(--font-display)] text-[32px] leading-[1.1] font-semibold tracking-[-0.02em] text-[var(--marketing-black)] sm:text-[44px]"
					style={{ textWrap: 'balance' }}
				>
					Your agent broke it. Let it read the finding.
				</h2>
				<p className="mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--marketing-gray)]">
					Free tier, one domain, weekly scans, no card. The MCP server is
					included at every tier.
				</p>
				<div className="mt-10 flex flex-wrap items-center justify-center gap-3">
					<Link to={href('/signup')} className="btn-marketing">
						<span className="flex items-center gap-2">
							Start free
							<span aria-hidden>→</span>
						</span>
					</Link>
					<Link to="/#pricing" className="btn-marketing-outline">
						See pricing
					</Link>
				</div>
			</div>
		</section>
	)
}
